import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Reset scroll position on route (PATH) change — promised to the PR #5030
 * reviewer: without it, clicking a door mid-page opens the spoke scrolled to
 * the old offset.
 *
 * Rules:
 * - Only a pathname change triggers the reset (hash-only changes like
 *   /#features → /#pricing are anchor jumps — leave them alone).
 * - If the new location carries a hash target (/#features from a spoke), skip
 *   the reset so the browser/anchor scroll wins.
 * - Locale switches (setLocale) pass state.preserveScroll — skipped.
 * - No scroll on initial mount (the ref starts at the current pathname), so
 *   browser scroll restoration on reload keeps working.
 */
export function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  const previousPathnameRef = useRef(pathname);

  useLayoutEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (hash) return;
    // Locale switches navigate to the same page under a prefixed path —
    // they set preserveScroll so the reader doesn't lose their position.
    if ((state as { preserveScroll?: boolean } | null)?.preserveScroll) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, state]);

  return null;
}
